import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckCircleIcon, MapPinIcon, MagnifyingGlassIcon, XMarkIcon, TagIcon, ChartBarIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { sendToWhatsApp } from '../../utils/whatsapp';

const HeroSection = () => {
  const { t } = useLanguage();
  const [selectedCity, setSelectedCity] = useState('');
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [citySearchTerm, setCitySearchTerm] = useState('');

  // Список городов Казахстана
  const kazakhstanCities = [
    'Алматы',
    'Астана (Нур-Султан)',
    'Шымкент',
    'Актобе',
    'Караганда',
    'Тараз',
    'Павлодар',
    'Усть-Каменогорск',
    'Семей',
    'Атырау',
    'Костанай',
    'Кызылорда',
    'Уральск',
    'Петропавловск',
    'Актау',
    'Темиртау',
    'Туркестан',
    'Кокшетау',
    'Талдыкорган',
    'Экибастуз',
    'Рудный',
    'Жанаозен',
    'Жезказган',
    'Балхаш',
    'Кентау',
    'Каскелен',
    'Сатпаев',
    'Степногорск',
    'Другой город'
  ];

  // Фильтрация городов по поисковому запросу
  const filteredCities = kazakhstanCities.filter(city =>
    city.toLowerCase().includes(citySearchTerm.toLowerCase())
  );

  const handleCitySelect = (city: string) => {
    setSelectedCity(city);
    setIsCityModalOpen(false);
    setCitySearchTerm('');
  };

  const handleQuickOrderSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!selectedCity) {
      alert(t('calc.cityAlert'));
      return;
    }
    
    const formData = new FormData(e.currentTarget);
    const pipeType = formData.get('pipeType');
    const diameter = formData.get('diameter');
    const sdr = formData.get('sdr');
    const length = formData.get('length');
    const deliveryType = formData.get('deliveryType');

    const message = `Здравствуйте! Хочу заказать трубу:\nТип: ${pipeType}\nДиаметр: ${diameter} мм\nSDR: ${sdr}\nДлина: ${length} м\nПоставка: ${deliveryType}\nГород: ${selectedCity}`;
    sendToWhatsApp(message, 'Быстрый расчет');
  };

  return (
    <section className="relative bg-white dark:bg-secondary-900 overflow-hidden min-h-screen">
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Background Image */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'url("/images/pe-pipes-production.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        ></div>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50/90 via-white/95 to-secondary-50/90 dark:from-secondary-900/90 dark:via-secondary-800/95 dark:to-secondary-900/90"></div>
        
        {/* Floating Shapes */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-primary-200 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-accent-orange/20 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-accent-green/20 rounded-full opacity-25 animate-bounce" style={{animationDelay: '1s'}}></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="h-full w-full" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #0ea5e9 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        {/* Pipe Pattern */}
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <div className="h-full w-full bg-gradient-to-l from-primary-300 to-transparent transform rotate-12 origin-top-right"></div>
        </div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-accent-orange/10 text-accent-orange text-sm font-medium animate-pulse">
              <TagIcon className="h-5 w-5 mr-1" /> {t('calc.discountBadge')}
            </div>
            
            {/* Static Content */}
            <div className="space-y-6">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-secondary-900 dark:text-white leading-tight">
                <span className="block mb-2">
                  {t('hero.company')}
                </span>
                <span className="text-primary-600 block text-2xl sm:text-3xl lg:text-4xl xl:text-5xl">
                  {t('hero.tagline')}
                </span>
              </h1>
              
              <p className="text-base sm:text-lg lg:text-xl text-secondary-600 dark:text-secondary-300 leading-relaxed max-w-2xl">
                {t('hero.description')}
              </p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-accent-green" />
                <span className="text-secondary-700 dark:text-secondary-300">{t('hero.fastDelivery')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-accent-green" />
                <span className="text-secondary-700 dark:text-secondary-300">{t('hero.qualityGuarantee')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-accent-green" />
                <span className="text-secondary-700 dark:text-secondary-300">{t('hero.certificates')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-accent-green" />
                <span className="text-secondary-700 dark:text-secondary-300">{t('hero.experience')}</span>
              </div>
            </div>
          
          </div>
          
          <div className="relative lg:-mt-8 xl:-mt-12">
            {/* Calculator Card */}
            <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-card p-6 lg:p-8 border border-secondary-200 dark:border-secondary-700 transform hover:scale-105 transition-transform duration-300">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-secondary-900 dark:text-white">{t('calc.title')}</h3>
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center animate-pulse">
                  <ChartBarIcon className="h-6 w-6 text-primary-600" />
                </div>
              </div>
              
              <form onSubmit={handleQuickOrderSubmit} className="space-y-4 lg:space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                    {t('calc.pipeType')}
                  </label>
                  <div className="relative">
                    <select
                      name="pipeType"
                      required
                      className="w-full px-4 py-2 lg:py-3 border-2 border-secondary-200 dark:border-secondary-600 rounded-xl focus:border-primary-500 focus:outline-none transition-all duration-200 hover:border-primary-300 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white appearance-none pr-10">
                      <option value="">{t('calc.selectType')}</option>
                      <option value="Водопроводная">{t('calc.water')}</option>
                      <option value="Канализационная">{t('calc.sewer')}</option>
                      <option value="Газопроводная">{t('calc.gas')}</option>
                      <option value="Для телекоммуникаций">{t('calc.telecom')}</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-5 w-5 text-secondary-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                      {t('calc.diameter')}
                    </label>
                    <input
                      type="number"
                      name="diameter"
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-secondary-200 dark:border-secondary-600 rounded-xl focus:border-primary-500 focus:outline-none transition-all duration-200 hover:border-primary-300 text-sm sm:text-base bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white"
                      placeholder="110"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                      {t('calc.sdr')}
                    </label>
                    <div className="relative">
                      <select
                        name="sdr"
                        required
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-secondary-200 dark:border-secondary-600 rounded-xl focus:border-primary-500 focus:outline-none transition-all duration-200 hover:border-primary-300 text-sm sm:text-base bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white appearance-none pr-10"
                      >
                        <option value="">{t('calc.selectSdr')}</option>
                        <option value="SDR 41">SDR 41</option>
                        <option value="SDR 33">SDR 33</option>
                        <option value="SDR 26">SDR 26</option>
                        <option value="SDR 21">SDR 21</option>
                        <option value="SDR 17.6">SDR 17.6</option>
                        <option value="SDR 17">SDR 17</option>
                        <option value="SDR 13.6">SDR 13.6</option>
                        <option value="SDR 11">SDR 11</option>
                        <option value="SDR 9">SDR 9</option>
                        <option value="SDR 7.4">SDR 7.4</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg className="h-5 w-5 text-secondary-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                      {t('calc.length')}
                    </label>
                    <input
                      type="number"
                      name="length"
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-secondary-200 dark:border-secondary-600 rounded-xl focus:border-primary-500 focus:outline-none transition-all duration-200 hover:border-primary-300 text-sm sm:text-base bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white"
                      placeholder="100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                      {t('calc.delivery')}
                    </label>
                    <div className="relative">
                    <select
                      name="deliveryType"
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-secondary-200 dark:border-secondary-600 rounded-xl focus:border-primary-500 focus:outline-none transition-all duration-200 hover:border-primary-300 text-sm sm:text-base bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white appearance-none pr-10"
                    >
                      <option value="">{t('calc.selectDelivery')}</option>
                      <option value="В бухтах">{t('calc.coils')}</option>
                      <option value="В отрезках">{t('calc.segments')}</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-5 w-5 text-secondary-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                      </svg>
                    </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                    {t('calc.city')}
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsCityModalOpen(true)}
                    className="w-full px-4 py-2 lg:py-3 border-2 border-secondary-200 dark:border-secondary-600 rounded-xl focus:border-primary-500 focus:outline-none transition-all duration-200 hover:border-primary-300 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white flex items-center justify-between"
                  >
                    <span className={selectedCity ? 'text-secondary-900 dark:text-white' : 'text-gray-500 dark:text-secondary-400'}>
                      {selectedCity || t('calc.selectCity')}
                    </span>
                    <MapPinIcon className="h-5 w-5 text-primary-500" />
                  </button>
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-accent-orange to-orange-600 text-white py-4 rounded-xl font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  {t('calc.submit')}
                </button>
              </form>

              <div className="mt-4 lg:mt-5 p-3 lg:p-4 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/50 dark:to-primary-800/50 rounded-xl">
                <p className="text-xs lg:text-sm text-primary-700 dark:text-primary-300 text-center">
                  <LightBulbIcon className="h-5 w-5 mr-1 inline" /> {t('calc.discount')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* City Selection Modal */}
      {isCityModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity bg-black bg-opacity-50" 
              onClick={() => setIsCityModalOpen(false)} 
            />
            
            <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-secondary-800 shadow-xl rounded-2xl relative">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <MapPinIcon className="h-6 w-6 text-primary-500 mr-3" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {t('calc.cityTitle')}
                  </h3>
                </div>
                <button
                  onClick={() => setIsCityModalOpen(false)}
                  className="text-gray-400 dark:text-secondary-400 hover:text-gray-600 dark:hover:text-secondary-300 transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Search Input */}
              <div className="mb-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400 dark:text-secondary-500" />
                  <input
                    type="text"
                    placeholder={t('calc.citySearch')}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 dark:border-secondary-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-secondary-700 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-secondary-400"
                    value={citySearchTerm}
                    onChange={(e) => setCitySearchTerm(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              {/* Cities List */}
              <div className="max-h-96 overflow-y-auto space-y-1 custom-scrollbar">
                {filteredCities.length > 0 ? (
                  filteredCities.map((city) => (
                    <button
                      key={city}
                      onClick={() => handleCitySelect(city)}
                      className="w-full text-left px-4 py-3 rounded-lg hover:bg-primary-50 dark:hover:bg-secondary-700 transition-colors duration-200 flex items-center group"
                    >
                      <MapPinIcon className="h-4 w-4 text-gray-400 dark:text-secondary-500 mr-3 group-hover:text-primary-500 transition-colors" />
                      <span className="text-gray-700 dark:text-secondary-300 group-hover:text-primary-600 dark:group-hover:text-primary-400 font-medium">
                        {city}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-secondary-400">
                      {t('calc.cityNotFound')}
                    </p>
                  </div>
                )}
              </div>

              {/* Popular Cities */}
              {!citySearchTerm && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-secondary-600">
                  <p className="text-sm font-semibold text-gray-600 dark:text-secondary-400 mb-2">
                    {t('calc.popularCities')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['Алматы', 'Астана (Нур-Султан)', 'Шымкент', 'Караганда', 'Актобе'].map((city) => (
                      <button
                        key={city}
                        onClick={() => handleCitySelect(city)}
                        className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-full text-sm font-medium hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors"
                      >
                        {city}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </section>
  );
};

export default HeroSection;